/**
 * DashboardModalContext
 *
 * Consolidates all modal state for the Dashboard to reduce prop drilling.
 * Instead of passing 60+ props through DashboardModals, components access
 * modal state and handlers via context hooks.
 *
 * @see TECHNICAL_DEBT_ROADMAP.md Phase 2.1
 */

import {
  createContext,
  useContext,
  useCallback,
  useState,
  useMemo,
  type ReactNode,
} from 'react';
import type { EnhancedAlert, TrackedProduct, NotificationChannel } from '../types/dashboard';
import type { TestDataProgress } from '../types/test-data';
import type { ExportFormat } from '../hooks/alerts';
import type { AlertActionModalState } from '../hooks/alerts';
import type { TabDescriptor } from '../components/dashboard/MonitoringCenter';

// =============================================================================
// Modal State Types
// =============================================================================

/**
 * Add Product modal state
 */
interface AddProductModalState {
  isOpen: boolean;
  trackedVariantIds: string[];
  preSelectedProductId?: string;
}

/**
 * Export modal state
 */
interface ExportModalState {
  isOpen: boolean;
  format: ExportFormat;
  fields: string[];
  isExporting: boolean;
  selectedTab: number;
  tabs: TabDescriptor[];
  filteredAlertsCount: number;
  filteredProductsCount: number;
}

/**
 * Alert Detail modal state
 */
interface AlertDetailModalState {
  isOpen: boolean;
  data: EnhancedAlert | null;
  hasNotificationChannels: boolean;
}

/**
 * Product Settings modal state
 */
interface ProductSettingsModalState {
  isOpen: boolean;
  selectedProduct: TrackedProduct | null;
  shopId: number;
}

/**
 * Test Data Progress modal state
 */
interface TestProgressModalState {
  isOpen: boolean;
  progress: TestDataProgress | null;
}

/**
 * Test Data Removal modal state
 */
interface TestRemovalModalState {
  isOpen: boolean;
  isDeleting: boolean;
  productCount: number;
  alertCount: number;
  velocityCount: number;
}

// =============================================================================
// Context Value Type
// =============================================================================

/**
 * Dashboard modal context value - all modal state and handlers
 */
export interface DashboardModalContextValue {
  // Shared state
  hasTestData: boolean;
  notificationChannels: NotificationChannel[];

  // Add Product Modal
  addProduct: AddProductModalState;
  openAddProduct: (preSelectedProductId?: string) => void;
  closeAddProduct: () => void;
  onSaveProducts: (toAdd: string[], toRemove: string[]) => Promise<void>;

  // Export Modal
  export: ExportModalState;
  openExport: () => void;
  closeExport: () => void;
  setExportFormat: (format: ExportFormat) => void;
  setExportFields: (fields: string[]) => void;
  onExport: () => void;

  // Alert Action Modals (resolve, dismiss, notify)
  alertAction: AlertActionModalState;
  openResolveConfirmation: (alert: EnhancedAlert) => void;
  openDismissConfirmation: (alert: EnhancedAlert) => void;
  openNotifyConfirmation: (alert: EnhancedAlert) => void;
  closeAlertModals: () => void;
  onAlertResolved: (alertId: number) => void;
  onAlertDismissed: (alertId: number) => void;
  onAlertNotified: (alertId: number, channelIds: number[]) => void;

  // Alert Detail Modal
  alertDetail: AlertDetailModalState;
  openAlertDetail: (alert: EnhancedAlert) => void;
  closeAlertDetail: () => void;
  onResolve: (alert: EnhancedAlert) => void;
  onDismiss: (alert: EnhancedAlert) => void;
  onNotify: (alert: EnhancedAlert) => void;

  // Product Settings Modal
  productSettings: ProductSettingsModalState;
  openProductSettings: (product: TrackedProduct) => void;
  closeProductSettings: () => void;
  onProductSettingsSaved: () => void;

  // Test Data Progress Modal
  testProgress: TestProgressModalState;
  openTestProgress: () => void;
  closeTestProgress: () => void;
  updateTestProgress: (progress: TestDataProgress) => void;

  // Test Data Removal Modal
  testRemoval: TestRemovalModalState;
  openTestRemoval: () => void;
  closeTestRemoval: () => void;
  onConfirmTestRemoval: () => Promise<void>;
}

// =============================================================================
// Context Creation
// =============================================================================

const DashboardModalContext = createContext<DashboardModalContextValue | null>(null);

/**
 * Hook to access dashboard modal context
 * @throws Error if used outside DashboardModalProvider
 */
export function useDashboardModals(): DashboardModalContextValue {
  const context = useContext(DashboardModalContext);
  if (!context) {
    throw new Error('useDashboardModals must be used within DashboardModalProvider');
  }
  return context;
}

// =============================================================================
// Provider Props
// =============================================================================

/**
 * Props for DashboardModalProvider - data and callbacks from parent
 */
export interface DashboardModalProviderProps {
  children: ReactNode;

  // Shared data from loader
  trackedVariantIds: string[];
  notificationChannels: NotificationChannel[];
  shopId: number;
  hasTestData: boolean;
  tabs: TabDescriptor[];
  selectedTab: number;
  filteredAlertsCount: number;
  filteredProductsCount: number;

  // Test data counts
  testProductCount: number;
  testAlertCount: number;
  testVelocityCount: number;

  // Callbacks from parent hooks
  onSaveProducts: (toAdd: string[], toRemove: string[]) => Promise<void>;
  onExport: () => void;
  setExportFormat: (format: ExportFormat) => void;
  setExportFields: (fields: string[]) => void;
  exportFormat: ExportFormat;
  exportFields: string[];
  isExporting: boolean;

  // Alert action callbacks
  openResolveConfirmation: (alert: EnhancedAlert) => void;
  openDismissConfirmation: (alert: EnhancedAlert) => void;
  openNotifyConfirmation: (alert: EnhancedAlert) => void;
  closeAlertModals: () => void;
  onAlertResolved: (alertId: number) => void;
  onAlertDismissed: (alertId: number) => void;
  onAlertNotified: (alertId: number, channelIds: number[]) => void;
  alertModalState: AlertActionModalState;

  // Product settings callback
  onProductSettingsSaved: () => void;

  // Test data callbacks
  onConfirmTestRemoval: () => Promise<void>;
  isDeleting: boolean;

  // Deep linking
  preSelectedProductId?: string;
  clearDeepLink: () => void;
}

// =============================================================================
// Provider Component
// =============================================================================

/**
 * Provider component for dashboard modal context
 */
export function DashboardModalProvider({
  children,
  // Shared data
  trackedVariantIds,
  notificationChannels,
  shopId,
  hasTestData,
  tabs,
  selectedTab,
  filteredAlertsCount,
  filteredProductsCount,
  testProductCount,
  testAlertCount,
  testVelocityCount,
  // Callbacks
  onSaveProducts,
  onExport,
  setExportFormat,
  setExportFields,
  exportFormat,
  exportFields,
  isExporting,
  openResolveConfirmation,
  openDismissConfirmation,
  openNotifyConfirmation,
  closeAlertModals,
  onAlertResolved,
  onAlertDismissed,
  onAlertNotified,
  alertModalState,
  onProductSettingsSaved,
  onConfirmTestRemoval,
  isDeleting,
  preSelectedProductId,
  clearDeepLink,
}: DashboardModalProviderProps) {
  // ==========================================================================
  // Internal Modal State
  // ==========================================================================

  // Add Product Modal
  const [addProductOpen, setAddProductOpen] = useState(false);

  // Export Modal
  const [exportOpen, setExportOpen] = useState(false);

  // Alert Detail Modal
  const [alertDetailOpen, setAlertDetailOpen] = useState(false);
  const [alertDetailData, setAlertDetailData] = useState<EnhancedAlert | null>(null);

  // Product Settings Modal
  const [productSettingsOpen, setProductSettingsOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<TrackedProduct | null>(null);

  // Test Data Progress Modal
  const [testProgressOpen, setTestProgressOpen] = useState(false);
  const [testProgressData, setTestProgressData] = useState<TestDataProgress | null>(null);

  // Test Data Removal Modal
  const [testRemovalOpen, setTestRemovalOpen] = useState(false);

  // ==========================================================================
  // Modal Handlers
  // ==========================================================================

  // Add Product Modal
  const openAddProduct = useCallback((selectedProductId?: string) => {
    // Note: preSelectedProductId is handled via props for deep linking
    setAddProductOpen(true);
  }, []);

  const closeAddProduct = useCallback(() => {
    setAddProductOpen(false);
    clearDeepLink();
  }, [clearDeepLink]);

  // Export Modal
  const openExport = useCallback(() => {
    setExportOpen(true);
  }, []);

  const closeExport = useCallback(() => {
    setExportOpen(false);
  }, []);

  // Alert Detail Modal
  const openAlertDetail = useCallback((alert: EnhancedAlert) => {
    setAlertDetailData(alert);
    setAlertDetailOpen(true);
  }, []);

  const closeAlertDetail = useCallback(() => {
    setAlertDetailOpen(false);
    setTimeout(() => setAlertDetailData(null), 150);
  }, []);

  // Alert action handlers that bridge to parent
  const onResolve = useCallback((alert: EnhancedAlert) => {
    openResolveConfirmation(alert);
  }, [openResolveConfirmation]);

  const onDismiss = useCallback((alert: EnhancedAlert) => {
    openDismissConfirmation(alert);
  }, [openDismissConfirmation]);

  const onNotify = useCallback((alert: EnhancedAlert) => {
    openNotifyConfirmation(alert);
  }, [openNotifyConfirmation]);

  // Product Settings Modal
  const openProductSettings = useCallback((product: TrackedProduct) => {
    setSelectedProduct(product);
    setProductSettingsOpen(true);
  }, []);

  const closeProductSettings = useCallback(() => {
    setProductSettingsOpen(false);
    setTimeout(() => setSelectedProduct(null), 150);
  }, []);

  // Test Data Progress Modal
  const openTestProgress = useCallback(() => {
    setTestProgressOpen(true);
  }, []);

  const closeTestProgress = useCallback(() => {
    setTestProgressOpen(false);
    setTimeout(() => setTestProgressData(null), 150);
  }, []);

  const updateTestProgress = useCallback((progress: TestDataProgress) => {
    setTestProgressData(progress);
  }, []);

  // Test Data Removal Modal
  const openTestRemoval = useCallback(() => {
    setTestRemovalOpen(true);
  }, []);

  const closeTestRemoval = useCallback(() => {
    setTestRemovalOpen(false);
  }, []);

  // ==========================================================================
  // Context Value
  // ==========================================================================

  const hasVerifiedChannels = notificationChannels.filter(c => c.enabled && c.is_verified).length > 0;

  const value = useMemo<DashboardModalContextValue>(() => ({
    // Shared state
    hasTestData,
    notificationChannels,

    // Add Product Modal
    addProduct: {
      isOpen: addProductOpen,
      trackedVariantIds,
      preSelectedProductId,
    },
    openAddProduct,
    closeAddProduct,
    onSaveProducts,

    // Export Modal
    export: {
      isOpen: exportOpen,
      format: exportFormat,
      fields: exportFields,
      isExporting,
      selectedTab,
      tabs,
      filteredAlertsCount,
      filteredProductsCount,
    },
    openExport,
    closeExport,
    setExportFormat,
    setExportFields,
    onExport,

    // Alert Action Modals
    alertAction: alertModalState,
    openResolveConfirmation,
    openDismissConfirmation,
    openNotifyConfirmation,
    closeAlertModals,
    onAlertResolved,
    onAlertDismissed,
    onAlertNotified,

    // Alert Detail Modal
    alertDetail: {
      isOpen: alertDetailOpen,
      data: alertDetailData,
      hasNotificationChannels: hasVerifiedChannels,
    },
    openAlertDetail,
    closeAlertDetail,
    onResolve,
    onDismiss,
    onNotify,

    // Product Settings Modal
    productSettings: {
      isOpen: productSettingsOpen,
      selectedProduct,
      shopId,
    },
    openProductSettings,
    closeProductSettings,
    onProductSettingsSaved,

    // Test Data Progress Modal
    testProgress: {
      isOpen: testProgressOpen,
      progress: testProgressData,
    },
    openTestProgress,
    closeTestProgress,
    updateTestProgress,

    // Test Data Removal Modal
    testRemoval: {
      isOpen: testRemovalOpen,
      isDeleting,
      productCount: testProductCount,
      alertCount: testAlertCount,
      velocityCount: testVelocityCount,
    },
    openTestRemoval,
    closeTestRemoval,
    onConfirmTestRemoval,
  }), [
    // Shared state
    hasTestData,
    notificationChannels,
    // Add Product
    addProductOpen,
    trackedVariantIds,
    preSelectedProductId,
    openAddProduct,
    closeAddProduct,
    onSaveProducts,
    // Export
    exportOpen,
    exportFormat,
    exportFields,
    isExporting,
    selectedTab,
    tabs,
    filteredAlertsCount,
    filteredProductsCount,
    openExport,
    closeExport,
    setExportFormat,
    setExportFields,
    onExport,
    // Alert Action
    alertModalState,
    openResolveConfirmation,
    openDismissConfirmation,
    openNotifyConfirmation,
    closeAlertModals,
    onAlertResolved,
    onAlertDismissed,
    onAlertNotified,
    // Alert Detail
    alertDetailOpen,
    alertDetailData,
    hasVerifiedChannels,
    openAlertDetail,
    closeAlertDetail,
    onResolve,
    onDismiss,
    onNotify,
    // Product Settings
    productSettingsOpen,
    selectedProduct,
    shopId,
    openProductSettings,
    closeProductSettings,
    onProductSettingsSaved,
    // Test Progress
    testProgressOpen,
    testProgressData,
    openTestProgress,
    closeTestProgress,
    updateTestProgress,
    // Test Removal
    testRemovalOpen,
    isDeleting,
    testProductCount,
    testAlertCount,
    testVelocityCount,
    openTestRemoval,
    closeTestRemoval,
    onConfirmTestRemoval,
  ]);

  return (
    <DashboardModalContext.Provider value={value}>
      {children}
    </DashboardModalContext.Provider>
  );
}

// =============================================================================
// Individual Modal Hooks
// =============================================================================

/**
 * Hook for Add Product modal
 */
export function useAddProductModal() {
  const ctx = useDashboardModals();
  return {
    isOpen: ctx.addProduct.isOpen,
    trackedVariantIds: ctx.addProduct.trackedVariantIds,
    preSelectedProductId: ctx.addProduct.preSelectedProductId,
    open: ctx.openAddProduct,
    close: ctx.closeAddProduct,
    onSave: ctx.onSaveProducts,
  };
}

/**
 * Hook for Export modal
 */
export function useExportModal() {
  const ctx = useDashboardModals();
  return {
    isOpen: ctx.export.isOpen,
    format: ctx.export.format,
    fields: ctx.export.fields,
    isExporting: ctx.export.isExporting,
    selectedTab: ctx.export.selectedTab,
    tabs: ctx.export.tabs,
    alertsCount: ctx.export.filteredAlertsCount,
    productsCount: ctx.export.filteredProductsCount,
    open: ctx.openExport,
    close: ctx.closeExport,
    setFormat: ctx.setExportFormat,
    setFields: ctx.setExportFields,
    onExport: ctx.onExport,
  };
}

/**
 * Hook for Alert Detail modal
 */
export function useAlertDetailModal() {
  const ctx = useDashboardModals();
  return {
    isOpen: ctx.alertDetail.isOpen,
    data: ctx.alertDetail.data,
    hasNotificationChannels: ctx.alertDetail.hasNotificationChannels,
    hasTestData: ctx.hasTestData,
    open: ctx.openAlertDetail,
    close: ctx.closeAlertDetail,
    onResolve: ctx.onResolve,
    onDismiss: ctx.onDismiss,
    onNotify: ctx.onNotify,
  };
}

/**
 * Hook for Alert Action modals (resolve, dismiss, notify)
 */
export function useAlertActionModals() {
  const ctx = useDashboardModals();
  return {
    state: ctx.alertAction,
    hasTestData: ctx.hasTestData,
    notificationChannels: ctx.notificationChannels,
    openResolve: ctx.openResolveConfirmation,
    openDismiss: ctx.openDismissConfirmation,
    openNotify: ctx.openNotifyConfirmation,
    close: ctx.closeAlertModals,
    onResolved: ctx.onAlertResolved,
    onDismissed: ctx.onAlertDismissed,
    onNotified: ctx.onAlertNotified,
  };
}

/**
 * Hook for Product Settings modal
 */
export function useProductSettingsModal() {
  const ctx = useDashboardModals();
  return {
    isOpen: ctx.productSettings.isOpen,
    product: ctx.productSettings.selectedProduct,
    shopId: ctx.productSettings.shopId,
    hasTestData: ctx.hasTestData,
    open: ctx.openProductSettings,
    close: ctx.closeProductSettings,
    onSaved: ctx.onProductSettingsSaved,
  };
}

/**
 * Hook for Test Data Progress modal
 */
export function useTestProgressModal() {
  const ctx = useDashboardModals();
  return {
    isOpen: ctx.testProgress.isOpen,
    progress: ctx.testProgress.progress,
    open: ctx.openTestProgress,
    close: ctx.closeTestProgress,
    updateProgress: ctx.updateTestProgress,
  };
}

/**
 * Hook for Test Data Removal modal
 */
export function useTestRemovalModal() {
  const ctx = useDashboardModals();
  return {
    isOpen: ctx.testRemoval.isOpen,
    isDeleting: ctx.testRemoval.isDeleting,
    productCount: ctx.testRemoval.productCount,
    alertCount: ctx.testRemoval.alertCount,
    velocityCount: ctx.testRemoval.velocityCount,
    open: ctx.openTestRemoval,
    close: ctx.closeTestRemoval,
    onConfirm: ctx.onConfirmTestRemoval,
  };
}
